import React, { useEffect, useState } from 'react';
import { Container, Typography, Box, Card, CardContent, IconButton, Button, Grid, Chip, useTheme, useMediaQuery, Paper, Stack, Skeleton, alpha, Tooltip } from '@mui/material';
import { getUserLocations, deleteUserLocation, setDefaultLocation } from './userLocationsApi';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import AddIcon from '@mui/icons-material/Add';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

function Locations() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    loadLocations();
  }, []);

  const loadLocations = async () => {
    try {
      setLoading(true);
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        const data = await getUserLocations(user.Id || user.id);
        setLocations(data);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmMessage = t('locations.deleteConfirm', 'Are you sure you want to delete this location?');
    if (window.confirm(confirmMessage)) {
      try {
        await deleteUserLocation(id);
        loadLocations();
      } catch (error) {
        console.error('Error deleting location:', error);
      }
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await setDefaultLocation(id);
      loadLocations();
    } catch (error) {
      console.error('Error setting default location:', error);
    }
  };

  return (
    <Container 
      maxWidth="md" 
      sx={{ 
        mt: { xs: 2, sm: 3, md: 4 }, 
        mb: { xs: 8, sm: 10 }, 
        px: { xs: 1.5, sm: 2, md: 3 },
        direction: i18n.dir() 
      }}
    >
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: { xs: 2, sm: 3 },
          bgcolor: 'primary.main',
          color: 'white',
          borderRadius: 3,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 2, sm: 0 }
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <HomeIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 'bold',
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' }
            }}
          >
            {t('locations.title', 'My Addresses')}
          </Typography>
        </Stack>
        
        <Button 
          variant="contained" 
          startIcon={<AddIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />} 
          onClick={() => navigate('/locations/add')}
          size={isMobile ? "medium" : "large"}
          sx={{ 
            bgcolor: 'white',
            color: 'primary.main',
            fontWeight: 700,
            width: { xs: '100%', sm: 'auto' },
            py: { xs: 1, sm: 1.2 },
            px: { xs: 2, sm: 3 },
            fontSize: { xs: '0.85rem', sm: '0.95rem' },
            '&:hover': { 
              bgcolor: alpha('#fff', 0.9),
              transform: 'scale(1.02)'
            },
            '&:active': { 
              transform: isMobile ? 'scale(0.98)' : 'scale(1.02)' 
            },
            transition: 'all 0.2s'
          }}
        >
          {t('locations.addNew', 'Add New')}
        </Button>
      </Paper>

      {/* Loading State */}
      {loading ? (
        <Grid container spacing={{ xs: 2, sm: 2.5 }}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} key={i}>
              <Skeleton 
                variant="rectangular" 
                height={isMobile ? 140 : 160} 
                sx={{ borderRadius: 3 }} 
              />
            </Grid>
          ))}
        </Grid>
      ) : locations.length === 0 ? (
        // Empty State
        <Paper
          elevation={2}
          sx={{ 
            textAlign: 'center', 
            py: { xs: 6, sm: 8 },
            px: { xs: 3, sm: 4 },
            borderRadius: 4,
            border: `2px dashed ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.primary.main, 0.03)
          }}
        >
          <LocationOnIcon 
            sx={{ 
              fontSize: { xs: 60, sm: 80 }, 
              color: 'text.disabled',
              mb: 2 
            }} 
          />
          <Typography 
            variant="h6" 
            color="textSecondary"
            sx={{ 
              mb: 3,
              fontSize: { xs: '1rem', sm: '1.25rem' }
            }}
          >
            {t('locations.empty', 'No addresses found. Add one now!')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/locations/add')}
            size={isMobile ? "medium" : "large"}
            sx={{
              py: { xs: 1.2, sm: 1.5 },
              px: { xs: 3, sm: 4 },
              fontSize: { xs: '0.9rem', sm: '1rem' },
              fontWeight: 700
            }}
          >
            {t('locations.addFirst', 'Add Your First Address')}
          </Button>
        </Paper>
      ) : (
        // Locations Grid
        <Grid container spacing={{ xs: 2, sm: 2.5 }}>
          {locations.map((loc, index) => (
            <Grid item xs={12} key={loc.Id}>
              <Card 
                sx={{ 
                  borderRadius: { xs: 3, sm: 4 }, 
                  boxShadow: loc.IsDefault ? 4 : 2,
                  border: '2px solid',
                  borderColor: loc.IsDefault ? 'primary.main' : 'divider',
                  bgcolor: loc.IsDefault ? alpha(theme.palette.primary.main, 0.03) : 'background.paper',
                  transition: 'all 0.3s ease',
                  animation: `fadeInUp 0.4s ease-out ${index * 0.1}s backwards`,
                  '@keyframes fadeInUp': {
                    from: { opacity: 0, transform: 'translateY(20px)' },
                    to: { opacity: 1, transform: 'translateY(0)' }
                  },
                  '&:hover': {
                    boxShadow: 6,
                    transform: isMobile ? 'none' : 'translateY(-4px)'
                  }
                }}
              >
                <CardContent sx={{ 
                  p: { xs: 2, sm: 3 },
                  '&:last-child': { pb: { xs: 2, sm: 3 } }
                }}>
                  <Stack 
                    direction={{ xs: 'column', sm: 'row' }} 
                    spacing={{ xs: 2, sm: 0 }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                  >
                    {/* Location Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack 
                        direction="row" 
                        spacing={{ xs: 1, sm: 1.5 }} 
                        alignItems="center" 
                        sx={{ mb: { xs: 1, sm: 1.5 } }}
                        flexWrap="wrap"
                      >
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 'bold',
                            fontSize: { xs: '1.1rem', sm: '1.25rem' },
                            color: loc.IsDefault ? 'primary.main' : 'text.primary'
                          }}
                        >
                          {loc.Name}
                        </Typography>
                        {loc.IsDefault && (
                          <Chip 
                            label={t('locations.default', 'Default')} 
                            size="small" 
                            color="primary"
                            icon={<StarIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />}
                            sx={{ 
                              fontWeight: 700,
                              fontSize: { xs: '0.7rem', sm: '0.75rem' },
                              height: { xs: 24, sm: 26 }
                            }} 
                          />
                        )}
                      </Stack>
                      
                      <Typography 
                        variant="body1"
                        sx={{ 
                          mb: { xs: 0.5, sm: 1 },
                          fontSize: { xs: '0.9rem', sm: '1rem' },
                          lineHeight: 1.6
                        }}
                      >
                        {loc.Address}
                      </Typography>
                      
                      <Stack 
                        direction="row" 
                        spacing={1} 
                        alignItems="center"
                      >
                        <LocationOnIcon 
                          sx={{ 
                            fontSize: { xs: 16, sm: 18 }, 
                            color: 'text.secondary' 
                          }} 
                        />
                        <Typography 
                          variant="body2" 
                          color="textSecondary"
                          sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
                        >
                          {loc.City}
                        </Typography>
                      </Stack>
                    </Box>

                    {/* Action Buttons */}
                    <Stack 
                      direction="row" 
                      spacing={{ xs: 0.5, sm: 1 }}
                      alignItems="center"
                      sx={{ 
                        alignSelf: { xs: 'flex-end', sm: 'center' },
                        flexShrink: 0
                      }}
                    >
                      {/* Star/Default Button */}
                      {!loc.IsDefault ? (
                        <Tooltip title={t('locations.setDefault', 'Set as Default')}>
                          <IconButton 
                            onClick={() => handleSetDefault(loc.Id)}
                            size={isMobile ? "small" : "medium"}
                            sx={{
                              color: 'text.secondary',
                              '&:hover': { 
                                color: 'warning.main',
                                bgcolor: alpha(theme.palette.warning.main, 0.1)
                              },
                              '&:active': { 
                                transform: 'scale(0.9)' 
                              }
                            }}
                          >
                            <StarBorderIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <IconButton 
                          disabled 
                          size={isMobile ? "small" : "medium"}
                          sx={{ color: 'warning.main' }}
                        >
                          <StarIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                        </IconButton>
                      )}
                      
                      {/* Edit Button */}
                      <Tooltip title={t('common.edit', 'Edit')}>
                        <IconButton 
                          onClick={() => navigate(`/locations/edit/${loc.Id}`)}
                          size={isMobile ? "small" : "medium"}
                          sx={{
                            color: 'primary.main',
                            '&:hover': { 
                              bgcolor: alpha(theme.palette.primary.main, 0.1)
                            },
                            '&:active': { 
                              transform: 'scale(0.9)' 
                            }
                          }}
                        >
                          <EditIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                        </IconButton>
                      </Tooltip>
                      
                      {/* Delete Button */}
                      <Tooltip title={t('common.delete', 'Delete')}>
                        <IconButton 
                          onClick={() => handleDelete(loc.Id)} 
                          color="error"
                          size={isMobile ? "small" : "medium"}
                          sx={{
                            '&:hover': { 
                              bgcolor: alpha(theme.palette.error.main, 0.1)
                            },
                            '&:active': { 
                              transform: 'scale(0.9)' 
                            }
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
}

export default Locations;
